import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./nav_bar/Navbar";
import { checkFreeDemoRequest, submitFreeDemoRequest } from "../api/freeDemoService";
import "./FreeDemoPage.css";

const SLOTS = [
  { value: "MORNING",   label: "🌅 Morning" },
  { value: "AFTERNOON", label: "☀️ Afternoon" },
  { value: "EVENING",   label: "🌙 Evening" },
];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

const slotLabel = (slot) => SLOTS.find((s) => s.value === slot)?.label || slot;

/* ── Status Card ── */
const StatusCard = ({ request, onRequestAgain }) => {
  const { status, preferredSlot, requestedAt, scheduledAt, zoomJoinUrl, zoomPassword } = request;

  if (status === "PENDING") return (
    <div className="fd-card fd-card-pending">
      <div className="fd-card-icon">🕐</div>
      <h2>Request Received!</h2>
      <p>Your free demo request is under review.<br />We'll send you the Zoom link within 24 hours.</p>
      {preferredSlot && <div className="fd-meta">Preferred Slot: <strong>{slotLabel(preferredSlot)}</strong></div>}
      <div className="fd-meta">Requested on: <strong>{fmtDate(requestedAt)}</strong></div>
    </div>
  );

  if (status === "SCHEDULED") return (
    <div className="fd-card fd-card-scheduled">
      <div className="fd-card-icon">✅</div>
      <h2>Demo Scheduled!</h2>
      <p>Your free demo class is confirmed.</p>
      <div className="fd-meta">📅 Date &amp; Time: <strong>{fmtDate(scheduledAt)}</strong></div>
      {zoomPassword && <div className="fd-meta">🔑 Password: <strong>{zoomPassword}</strong></div>}
      {zoomJoinUrl && (
        <a href={zoomJoinUrl} target="_blank" rel="noreferrer" className="fd-btn fd-btn-primary">
          Join Demo on Zoom →
        </a>
      )}
    </div>
  );

  if (status === "COMPLETED") return (
    <div className="fd-card fd-card-completed">
      <div className="fd-card-icon">🎉</div>
      <h2>Demo Completed!</h2>
      <p>Hope you enjoyed your free demo class.</p>
      <a href="/courses" className="fd-btn fd-btn-primary">Book a Course →</a>
    </div>
  );

  if (status === "CANCELLED") return (
    <div className="fd-card fd-card-cancelled">
      <div className="fd-card-icon">❌</div>
      <h2>Request Cancelled</h2>
      <p>Your demo request was cancelled.</p>
      <button className="fd-btn fd-btn-primary" onClick={onRequestAgain}>Request Again →</button>
    </div>
  );

  return null;
};

/* ── Request Form ── */
const RequestForm = ({ onSuccess }) => {
  const [slot, setSlot]       = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const res = await submitFreeDemoRequest({
        ...(slot    && { preferredSlot: slot }),
        ...(message.trim() && { message: message.trim() }),
      });
      onSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fd-form-wrap">
      <div className="fd-form-header">
        <div className="fd-form-icon">🎓</div>
        <h2>Book a Free Demo</h2>
        <p>Experience a live class before you commit. No payment required.</p>
      </div>

      {error && <div className="fd-alert">{error}</div>}

      <form onSubmit={handleSubmit} className="fd-form">
        <div className="fd-form-group">
          <label>Preferred Time Slot <span className="fd-optional">(optional)</span></label>
          <div className="fd-slot-group">
            {SLOTS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`fd-slot-btn ${slot === s.value ? "active" : ""}`}
                onClick={() => setSlot(slot === s.value ? "" : s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="fd-form-group">
          <label>Anything you'd like us to know? <span className="fd-optional">(optional)</span></label>
          <textarea
            className="fd-textarea"
            rows={3}
            placeholder="e.g. I'm a beginner, I want to improve my pronunciation..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <button type="submit" className="fd-btn fd-btn-primary fd-btn-full" disabled={submitting}>
          {submitting ? "Submitting…" : "Request Free Demo"}
        </button>
      </form>
    </div>
  );
};

/* ── Main Page ── */
const FreeDemoPage = () => {
  const navigate = useNavigate();
  const [state, setState]     = useState("loading"); // loading | form | status
  const [request, setRequest] = useState(null);

  useEffect(() => {
    checkFreeDemoRequest()
      .then((res) => { setRequest(res.data); setState("status"); })
      .catch((err) => {
        if (err.response?.status === 404) setState("form");
        else if (err.response?.status === 401) navigate("/login?redirect=/free-demo", { replace: true });
        else setState("form");
      });
  }, [navigate]);

  return (
    <>
      <Navbar />
      <div className="fd-page">
        {state === "loading" && (
          <div className="fd-loading">
            <div className="fd-spinner" />
            <p>Checking your request…</p>
          </div>
        )}
        {state === "form" && (
          <RequestForm onSuccess={(data) => { setRequest(data); setState("status"); }} />
        )}
        {state === "status" && request && (
          <StatusCard
            request={request}
            onRequestAgain={() => { setRequest(null); setState("form"); }}
          />
        )}
      </div>
    </>
  );
};

export default FreeDemoPage;
